import React from 'react';
import { toast } from '../../utils/customToast';
import { 
    FiCheckCircle as CheckCircleIcon,
    FiAlertTriangle as ExclamationTriangleIcon
} from 'react-icons/fi';

// Helper function to show restriction toasts
export const showRestrictedActionToast = (message) => {
    toast.warning(message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
    });
};

// Status configuration
const STATUS_CONFIG = {
    active: {
        label: 'Actif',
        color: 'text-green-600 dark:text-green-400',
        bgColor: 'bg-green-100 dark:bg-green-900/30',
        borderColor: 'border-green-300 dark:border-green-600',
        icon: CheckCircleIcon,
        description: 'Votre compte est actif'
    },
    suspended: {
        label: 'Suspendu',
        color: 'text-orange-600 dark:text-orange-400',
        bgColor: 'bg-orange-100 dark:bg-orange-900/30',
        borderColor: 'border-orange-300 dark:border-orange-600',
        icon: ExclamationTriangleIcon,
        description: 'Problème de paiement - Accès limité'
    },
    trial: {
        label: 'Essai',
        color: 'text-blue-600 dark:text-blue-400',
        bgColor: 'bg-blue-100 dark:bg-blue-900/30',
        borderColor: 'border-blue-300 dark:border-blue-600',
        icon: CheckCircleIcon,
        description: 'Période d’essai en cours'
    }
};

const TenantStatus = ({ 
    tenant, 
    className = "",
    showDescription = true,
    compact = false,
    onUpdatePayment,
    onReactivateSubscription,
    onChoosePlan
}) => {
    if (!tenant || !tenant.status) return null;

    const status = tenant.status;
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.active;
    const IconComponent = config.icon;

    if (compact) {
        return (
            <div className={`inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${config.bgColor} ${config.color} ${config.borderColor} border ${className}`}>
                <IconComponent className="w-4 h-4 mr-1" />
                {config.label}
            </div>
        );
    }

    return (
        <div className={`p-4 rounded-lg border ${config.bgColor} ${config.borderColor} ${className}`}>
            <div className="flex items-start space-x-3">
                <IconComponent className={`w-5 h-5 ${config.color} flex-shrink-0 mt-0.5`} />
                <div className="flex-1">
                    <h3 className={`text-sm font-medium ${config.color}`}>
                        Statut: {config.label}
                    </h3>
                    {showDescription && (
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                            {config.description}
                        </p>
                    )}
                    
                    {/* Actions spécifiques selon le statut */}
                    {status === 'suspended' && (
                        <div className="mt-3">
                            <p className="text-sm text-orange-800 dark:text-orange-200">
                                Votre accès est limité en raison d'un problème de paiement. 
                            </p>
                            <button 
                                onClick={onUpdatePayment}
                                className="mt-2 text-sm font-medium text-orange-700 dark:text-orange-300 hover:text-orange-900 dark:hover:text-orange-100 underline"
                            >
                                Mettre à jour le paiement
                            </button>
                        </div>
                    )}
                    


                </div>
            </div>
        </div>
    );
};

// Component pour afficher le statut dans la barre de navigation ou header
export const TenantStatusBadge = ({ tenant, onClick }) => {
    if (!tenant || tenant.status === 'active') return null;

    const status = tenant.status;
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.trial || STATUS_CONFIG.suspended;
    const IconComponent = config.icon || CheckCircleIcon;

    const handleClick = (e) => {
        e.preventDefault();
        if (onClick) onClick();
    };

    return (
        <button 
            onClick={handleClick}
            className={`inline-flex items-center px-2 py-1 rounded-md text-sm font-medium ${config.bgColor} ${config.color} ${config.borderColor} border hover:opacity-80 transition-opacity`}
            title={config.description}
        >
            <IconComponent className="w-3 h-3 mr-1" />
            {config.label}
        </button>
    );
};

// Component pour les restrictions d'accès
export const StatusAccessRestriction = ({ tenant, action, children, onRestrictedAction }) => {
    if (!tenant) return null;

    const isRestricted = (status, action) => {
        if (status === 'suspended') {
            const restrictedActions = ['create_message', 'create_todo', 'export', 'import'];
            return restrictedActions.includes(action);
        }
        
        return false;
    };

    const restricted = isRestricted(tenant.status, action);
    
    if (!restricted) {
        return children;
    }

    // Fonction pour déclencher le toast quand l'utilisateur clique sur un élément restreint
    const handleRestrictedClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (onRestrictedAction) {
            const message = tenant.status === 'suspended' 
                ? 'Fonctionnalité indisponible - Organisation suspendue'
                : 'Fonctionnalité indisponible';
            onRestrictedAction(message);
        }
    };
    
    return (
        <div className="relative" onClick={handleRestrictedClick}>
            <div className="opacity-50 pointer-events-auto cursor-not-allowed">
                {children}
            </div>
        </div>
    );
};

export default TenantStatus;